import {
  formatDateRange,
  formatDayMonth,
  parseDisplayDate,
  portalRequestName,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

export const formatBytes = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

/* Every date the portal shows is the house date from `formatDayMonth`: the
   day, then the short month, "20 Sep". A date from another year carries its
   year, "20 Sep 2025", so an old file never reads as this year's. */

/* A date-only value ("2026-08-31") is a calendar day, not UTC midnight;
   parseDisplayDate keeps it on its own day in every time zone. */
const displayDate = (value: string): Date =>
  parseDisplayDate(value) ?? new Date(value)

/** The house date, with the time when asked: "20 Sep, 3:45 PM". */
export const houseDate = (
  value: string,
  options: { time?: boolean; now?: Date } = {}
): string => {
  const date = displayDate(value)
  const now = options.now ?? new Date()
  return formatDayMonth(date, {
    year: date.getFullYear() !== now.getFullYear(),
    time: options.time ?? false,
  })
}

/** "20 Sep, 3:45 PM". */
export const formatDateTime = (value: string): string =>
  houseDate(value, { time: true })

/** "20 Sep". */
export const formatShortDate = (value: string): string => houseDate(value)

/** The request's name: what it asks for, never a month. */
export const requestLabel = (request: PortalRequestResponse): string =>
  portalRequestName(request)

/** The request and the records it covers, e.g. "Year-end · 1 Jan to 31 Dec
 * 2025". A range the name already says is left off. */
export const requestLine = (request: PortalRequestResponse): string => {
  const label = requestLabel(request)
  const range = formatDateRange(request.coverageStart, request.coverageEnd)
  return range && !label.includes(range) ? `${label} · ${range}` : label
}

const smallNumbers = [
  "no",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
]

/** A sentence counts in words; a column counts in digits. Only the lede and
 * the lines under it use this. */
export const countInWords = (count: number): string =>
  smallNumbers[count] ?? String(count)

export const plural = (count: number, one: string, many: string): string =>
  count === 1 ? one : many
